import { shippingTextSchema } from "@/lib/shipping-text";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  type CreateContactInput,
  type CustomerContact,
  PropertyType,
} from "../types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CustomerSearch } from "@/features/customers/customer-search";
import { useParseContact } from "../api";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { geocodeAddress } from "@/services/google-geocoding";

const contactFormSchema = z.object({
  customerId: z.string().min(1, "Customer ID is required").optional(), // For admin use
  contactName: shippingTextSchema.min(1, "Name is required").max(100),
  contactCompany: shippingTextSchema.max(100).optional(),
  contactPhone: z.string().max(50).optional(),
  contactEmail: z.string().email("Invalid email").or(z.literal("")).optional(),
  addressLine1: shippingTextSchema
    .min(1, "Address line 1 is required")
    .max(200),
  addressLine2: shippingTextSchema.max(200).optional(),
  addressCity: shippingTextSchema.min(1, "City is required").max(100),
  addressProvince: z.string().min(1, "Province/State is required").max(100),
  addressCountry: z.string().min(1, "Country is required").max(100),
  addressPostalCode: z.string().max(20).optional(),
  label: z.string().max(50).optional(),
  pickupInstructions: z.string().max(500).optional(),
  deliveryInstructions: z.string().max(500).optional(),
  isDefault: z.boolean(),
  isFavorite: z.boolean(),
  propertyResidential: z.boolean(),
  propertyType: z.nativeEnum(PropertyType).optional(),
});

type ContactFormValues = z.infer<typeof contactFormSchema>;

interface ContactFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateContactInput & { customerId?: string }) => void;
  initialData?: CustomerContact;
  isLoading?: boolean;
  selectedCustomerId?: string;
}

export function ContactForm({
  open,
  onClose,
  onSubmit,
  initialData,
  isLoading = false,
  selectedCustomerId,
}: ContactFormProps) {
  const [smartInput, setSmartInput] = useState("");
  const [showUnverifiedConfirm, setShowUnverifiedConfirm] = useState(false);
  const [pendingValues, setPendingValues] = useState<ContactFormValues | null>(
    null
  );
  const [pendingLocation, setPendingLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [isGeocoding, setIsGeocoding] = useState(false);
  const parseContactMutation = useParseContact();

  const getDefaultValues = (): ContactFormValues => ({
    customerId: selectedCustomerId ?? "",
    contactName: "",
    contactCompany: "",
    contactPhone: "",
    contactEmail: "",
    addressLine1: "",
    addressLine2: "",
    addressCity: "",
    addressProvince: "",
    addressCountry: "Canada",
    addressPostalCode: "",
    label: "",
    pickupInstructions: "",
    deliveryInstructions: "",
    isDefault: false,
    isFavorite: false,
    propertyResidential: true,
  });

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: initialData ?? getDefaultValues(),
  });

  // Reset form when dialog opens or initialData changes
  useEffect(() => {
    if (open) {
      form.reset(initialData ?? getDefaultValues());
      setSmartInput("");
    }
  }, [open, initialData]);

  const handleSmartParse = async () => {
    if (!smartInput.trim()) {
      toast.error("No input", {
        description: "Please enter contact information to parse",
      });
      return;
    }

    try {
      const parsed = await parseContactMutation.mutateAsync(smartInput);

      // Update form with parsed data
      if (parsed.name) form.setValue("contactName", parsed.name);
      if (parsed.company) form.setValue("contactCompany", parsed.company);
      if (parsed.phone) form.setValue("contactPhone", parsed.phone);
      if (parsed.email) form.setValue("contactEmail", parsed.email);
      if (parsed.address?.street1)
        form.setValue("addressLine1", parsed.address.street1);
      if (parsed.address?.street2)
        form.setValue("addressLine2", parsed.address.street2);
      if (parsed.address?.city)
        form.setValue("addressCity", parsed.address.city);
      if (parsed.address?.province)
        form.setValue("addressProvince", parsed.address.province);
      if (parsed.address?.postalCode)
        form.setValue("addressPostalCode", parsed.address.postalCode);
      if (parsed.address?.country)
        form.setValue("addressCountry", parsed.address.country);
      if (parsed.label) form.setValue("label", parsed.label);
      if (parsed.pickupInstructions)
        form.setValue("pickupInstructions", parsed.pickupInstructions);
      if (parsed.deliveryInstructions)
        form.setValue("deliveryInstructions", parsed.deliveryInstructions);

      // Map property type
      if (parsed.propertyType === "Residential") {
        form.setValue("propertyResidential", true);
      } else if (parsed.propertyType === "Commercial") {
        form.setValue("propertyResidential", false);
      }

      // Clear smart input after successful parse
      setSmartInput("");

      toast.success("Contact parsed successfully", {
        description:
          "Form fields have been populated with the extracted information",
      });
    } catch (error: unknown) {
      console.error("Error parsing contact:", error);
      const err = error as { response?: { data?: { message?: string } } };
      toast.error("Parsing failed", {
        description:
          err.response?.data?.message ?? "Failed to parse contact information",
      });
    }
  };

  const submitWithLocation = (
    values: ContactFormValues,
    location: { lat: number; lng: number }
  ) => {
    const contactData = {
      ...values,
      location,
    } as CreateContactInput;
    onSubmit(contactData);
  };

  const handleConfirmUnverified = () => {
    if (pendingValues && pendingLocation) {
      submitWithLocation(pendingValues, pendingLocation);
    }
    setShowUnverifiedConfirm(false);
    setPendingValues(null);
    setPendingLocation(null);
  };

  const handleSubmit = async (values: ContactFormValues) => {
    setIsGeocoding(true);
    try {
      // Geocode the address to get proper coordinates
      const geocodeResult = await geocodeAddress({
        street1: values.addressLine1,
        street2: values.addressLine2,
        city: values.addressCity,
        state: values.addressProvince,
        postalCode: values.addressPostalCode ?? "",
        country: values.addressCountry,
      });

      if (!geocodeResult) {
        // Complete failure - show confirmation with fallback coordinates
        setPendingValues(values);
        setPendingLocation({ lat: 0, lng: 0 });
        setConfirmMessage(
          "We couldn't find this address in Google Maps. The address may be new, misspelled, or not in Google's database."
        );
        setShowUnverifiedConfirm(true);
        return;
      }

      const location = {
        lat: geocodeResult.latitude,
        lng: geocodeResult.longitude,
      };

      // Check if address is approximate (not precise/rooftop match)
      if (geocodeResult.isApproximate) {
        setPendingValues(values);
        setPendingLocation(location);
        setConfirmMessage(
          `This address could not be precisely located. Google returned an approximate location. The exact street number may not exist.`
        );
        setShowUnverifiedConfirm(true);
        return;
      }

      submitWithLocation(values, location);
    } catch (error) {
      console.error("Error during geocoding:", error);
      // Show confirmation dialog on error too
      setPendingValues(values);
      setPendingLocation({ lat: 0, lng: 0 });
      setConfirmMessage("An error occurred while verifying the address.");
      setShowUnverifiedConfirm(true);
    } finally {
      setIsGeocoding(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {initialData ? "Edit Contact" : "Add New Contact"}
            </DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-4"
            >
              {/* Smart Input - AI-powered parsing */}
              {!initialData && (
                <div className="space-y-3 p-4 bg-linear-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-purple-600" />
                    <h3 className="font-semibold text-gray-900">
                      Smart Input (AI-Powered)
                    </h3>
                  </div>
                  <p className="text-sm text-gray-600">
                    Paste contact information in any format. Our AI will
                    automatically extract and fill the form fields.
                  </p>
                  <div className="space-y-2">
                    <Textarea
                      value={smartInput}
                      onChange={(e) => setSmartInput(e.target.value)}
                      placeholder="Paste contact information here..."
                      rows={4}
                      className="bg-white"
                    />
                    <p className="text-xs text-muted-foreground">
                      <span className="font-medium">Example:</span> John Doe
                      from Acme Corp, email john@acme.com, phone 555-1234, 123
                      Main St, Toronto ON M5V 1A1, office address, leave at
                      reception
                    </p>
                    <Button
                      type="button"
                      onClick={handleSmartParse}
                      disabled={
                        !smartInput.trim() || parseContactMutation.isPending
                      }
                      className="w-full"
                      variant="secondary"
                    >
                      {parseContactMutation.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Parsing...
                        </>
                      ) : (
                        <>
                          <Sparkles className="mr-2 h-4 w-4" />
                          Parse with AI
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Customer Selector - when creating new contact */}
              {!initialData && (
                <div className="space-y-4">
                  <h3 className="font-semibold">Customer</h3>
                  <FormField
                    control={form.control}
                    name="customerId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Customer *</FormLabel>
                        <CustomerSearch
                          value={field.value}
                          onValueChange={field.onChange}
                          placeholder="Select customer..."
                          disabled={!!selectedCustomerId}
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="font-semibold">Contact Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="contactName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="John Doe" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="contactCompany"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Acme Inc." />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="contactPhone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone</FormLabel>
                        <FormControl>
                          <PhoneInput
                            placeholder="Phone Number"
                            defaultCountry="CA"
                            {...field}
                            value={field.value}
                            onChange={(value) => field.onChange(value || "")}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="contactEmail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="john@example.com"
                            type="email"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Address */}
              <div className="space-y-4">
                <h3 className="font-semibold">Address</h3>
                <FormField
                  control={form.control}
                  name="addressLine1"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address Line 1 *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="123 Main St" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="addressLine2"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address Line 2</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Apt 4B" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="addressCity"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>City *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Toronto" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="addressProvince"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Province/State *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="ON" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="addressPostalCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Postal Code</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="M5V 3A8" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="addressCountry"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Country *</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Metadata */}
              <div className="space-y-4">
                <h3 className="font-semibold">Additional Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="label"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Label</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a label" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Home">Home</SelectItem>
                            <SelectItem value="Office">Office</SelectItem>
                            <SelectItem value="Warehouse">Warehouse</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="propertyType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Property Type</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select property type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value={PropertyType.HOUSE}>
                              House
                            </SelectItem>
                            <SelectItem value={PropertyType.CONDO_LOBBY}>
                              Condo Lobby
                            </SelectItem>
                            <SelectItem value={PropertyType.CONDO_UNIT}>
                              Condo Unit
                            </SelectItem>
                            <SelectItem value={PropertyType.OFFICE}>
                              Office
                            </SelectItem>
                            <SelectItem value={PropertyType.WAREHOUSE}>
                              Warehouse
                            </SelectItem>
                            <SelectItem value={PropertyType.RETAIL}>
                              Retail
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="pickupInstructions"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pickup Instructions</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="Instructions for people picking up from this address..."
                          rows={3}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="deliveryInstructions"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Default delivery instructions</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="Instructions for people delivering to this address..."
                          rows={3}
                        />
                      </FormControl>
                      <p className="text-sm text-muted-foreground">
                        Prefills new orders. Editing this contact does not
                        change existing Shipments.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex items-center gap-6">
                  <FormField
                    control={form.control}
                    name="isDefault"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormLabel className="mt-0!">Set as Default</FormLabel>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="isFavorite"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormLabel className="mt-0!">
                          Add to Favorites
                        </FormLabel>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="propertyResidential"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormLabel className="mt-0!">Residential</FormLabel>
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isLoading || isGeocoding}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading || isGeocoding}>
                  {isLoading || isGeocoding ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {isGeocoding ? "Verifying address..." : "Saving..."}
                    </>
                  ) : initialData ? (
                    "Update Contact"
                  ) : (
                    "Add Contact"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={showUnverifiedConfirm}
        onOpenChange={setShowUnverifiedConfirm}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Address could not be verified</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmMessage} Do you want to save this contact anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setPendingValues(null);
                setPendingLocation(null);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmUnverified}>
              Save Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
